import * as THREE from 'three';
import { C, OreKind, PlanetDef } from './constants';

export type BlockKind = 'soil' | 'rock' | 'deepRock' | 'lava' | 'gas' | OreKind;

export interface Block {
  kind: BlockKind;
  hp: number;
  maxHp: number;
  alive: boolean;
}

/** Deterministic LCG so a seed reproduces the same planet. */
function lcg(seed: number) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff);
}

/**
 * One planet: a cols×rows block grid from the surface (row 0, y≈0) downward,
 * rendered as one InstancedMesh with per-instance color, plus the gradient
 * sky, cave back-wall, surface scatter and background landforms. Palette and
 * ore character come from the PlanetDef.
 */
export class World {
  readonly group = new THREE.Group();
  readonly blocks: (Block | null)[][] = [];   // [row][col]
  private mesh!: THREE.InstancedMesh;
  private readonly color = new THREE.Color();
  private readonly dummy = new THREE.Object3D();
  private skyUniforms!: { topColor: { value: THREE.Color }; bottomColor: { value: THREE.Color } };
  surfaceRow0Y = -0.5;

  constructor(readonly planet: PlanetDef) {}

  colToX(col: number): number { return col - C.world.cols / 2 + 0.5; }
  rowToY(row: number): number { return this.surfaceRow0Y - row; }
  xToCol(x: number): number { return Math.round(x + C.world.cols / 2 - 0.5); }
  yToRow(y: number): number { return Math.round(this.surfaceRow0Y - y); }

  blockAt(col: number, row: number): Block | null {
    if (row < 0 || row >= C.world.rows || col < 0 || col >= C.world.cols) return null;
    return this.blocks[row][col];
  }

  solidAtWorld(x: number, y: number): boolean {
    const b = this.blockAt(this.xToCol(x), this.yToRow(y));
    return !!b && b.alive;
  }

  surfaceYAt(x: number): number {
    const col = THREE.MathUtils.clamp(this.xToCol(x), 0, C.world.cols - 1);
    for (let row = 0; row < C.world.rows; row++) {
      const b = this.blocks[row][col];
      if (b && b.alive) return this.rowToY(row) + 0.5;
    }
    return this.rowToY(C.world.rows);
  }

  build(): void {
    const rnd = lcg(this.planet.seed);

    for (let row = 0; row < C.world.rows; row++) {
      const line: (Block | null)[] = [];
      for (let col = 0; col < C.world.cols; col++) {
        line.push(this.rollBlock(row, rnd));
      }
      this.blocks.push(line);
    }
    for (let i = 0; i < 26; i++) {
      const cr = 12 + Math.floor(rnd() * (C.world.rows - 30));
      const cc = 4 + Math.floor(rnd() * (C.world.cols - 8));
      const r = 1 + rnd() * 2.6;
      for (let dr = -3; dr <= 3; dr++)
        for (let dc = -3; dc <= 3; dc++)
          if (dr * dr + dc * dc <= r * r) {
            const b = this.blockAt(cc + dc, cr + dr);
            if (b) b.alive = false;
          }
    }

    this.buildMesh();
    this.buildCaveWall();
    this.buildSky();
    this.buildDressing();
    this.buildSurfaceScatter();
  }

  dispose(): void {
    this.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else if (mat) mat.dispose();
    });
    this.group.removeFromParent();
  }

  private rollBlock(row: number, rnd: () => number): Block {
    const r = rnd();
    const p = this.planet;
    let kind: BlockKind;
    const depth = row;
    const rich = p.oreRichness;
    const haz = p.hazard;

    if (depth < 3) kind = 'soil';
    else if (depth > 80 && r < 0.012 * haz) kind = 'lava';
    else if (depth > 26 && r < (0.02 + depth * 0.0001) * haz) kind = 'gas';
    else if (depth > 170 && r < 0.016 * rich) kind = 'exotic';
    else if (depth > 105 && r < 0.02 * rich) kind = 'crystal';
    else if (depth > 55 && r < 0.03 * rich) kind = 'gold';
    else if (depth > 22 && r < 0.045 * rich) kind = 'silver';
    else if (depth > 3 && r < 0.06 * rich) kind = 'copper';
    else kind = depth > 120 ? 'deepRock' : 'rock';

    const hp = (kind === 'soil' ? 0.7
      : kind === 'gas' || kind === 'lava' ? 1
      : 1 + depth / 60) * (kind === 'soil' ? 1 : p.hardness);
    return { kind, hp, maxHp: hp, alive: true };
  }

  private idx(row: number, col: number): number { return row * C.world.cols + col; }

  private buildMesh(): void {
    const geo = new THREE.BoxGeometry(0.98, 0.98, 2.4);
    const mat = new THREE.MeshLambertMaterial();
    const count = C.world.rows * C.world.cols;
    this.mesh = new THREE.InstancedMesh(geo, mat, count);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    for (let row = 0; row < C.world.rows; row++)
      for (let col = 0; col < C.world.cols; col++)
        this.refreshInstance(row, col);
    this.mesh.instanceColor!.needsUpdate = true;
    this.group.add(this.mesh);
  }

  refreshInstance(row: number, col: number): void {
    const i = this.idx(row, col);
    const b = this.blocks[row][col];
    if (!b || !b.alive) {
      this.dummy.position.set(0, 10_000, 0);
      this.dummy.scale.setScalar(0.0001);
    } else {
      this.dummy.position.set(this.colToX(col), this.rowToY(row), 0);
      this.dummy.scale.setScalar(1);
      this.blockColor(b, row, col, this.color);
      const dmg = 1 - b.hp / b.maxHp;
      if (dmg > 0.01) this.color.lerp(new THREE.Color(0xff5a1f), dmg * 0.75);
      this.mesh.setColorAt(i, this.color);
    }
    this.dummy.updateMatrix();
    this.mesh.setMatrixAt(i, this.dummy.matrix);
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  private blockColor(b: Block, row: number, col: number, out: THREE.Color): void {
    const p = this.planet;
    const jitter = ((row * 31 + col * 17) % 7) / 7;
    switch (b.kind) {
      case 'soil': out.set(p.soil).offsetHSL(0, 0, jitter * 0.05); break;
      case 'rock': out.set(p.rocks[(row + col) % p.rocks.length]); break;
      case 'deepRock': out.set(p.deepRocks[(row + col) % p.deepRocks.length]); break;
      case 'lava': out.set(0xd8451c); break;
      case 'gas': out.set(0x87b06a); break;
      default:
        // Ores glow brighter than the lit rock around them
        out.set(C.ore[b.kind as OreKind].color).multiplyScalar(1.35);
    }
    out.offsetHSL(0, 0, -row * 0.0006);
  }

  damage(col: number, row: number, amount: number): BlockKind | null {
    const b = this.blockAt(col, row);
    if (!b || !b.alive) return null;
    b.hp -= amount;
    if (b.hp > 0) {
      this.refreshInstance(row, col);
      return null;
    }
    b.alive = false;
    this.refreshInstance(row, col);
    return b.kind;
  }

  // ---------- backdrop ----------

  private buildCaveWall(): void {
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(C.world.cols + 2, C.world.rows + 2),
      new THREE.MeshLambertMaterial({ color: this.planet.caveWall }),
    );
    wall.position.set(0, this.rowToY(C.world.rows / 2) + 0.5, -1.25);
    this.group.add(wall);
  }

  private buildSky(): void {
    // Vertical gradient shader — far richer than a flat color.
    this.skyUniforms = {
      topColor: { value: new THREE.Color(this.planet.skyHigh) },
      bottomColor: { value: new THREE.Color(this.planet.skyLow) },
    };
    const sky = new THREE.Mesh(
      new THREE.PlaneGeometry(1600, 1100),
      new THREE.ShaderMaterial({
        uniforms: this.skyUniforms,
        depthWrite: false,
        vertexShader: `
          varying float vY;
          void main() {
            vY = position.y;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }`,
        fragmentShader: `
          uniform vec3 topColor;
          uniform vec3 bottomColor;
          varying float vY;
          void main() {
            float t = clamp((vY + 300.0) / 750.0, 0.0, 1.0);
            gl_FragColor = vec4(mix(bottomColor, topColor, t), 1.0);
          }`,
      }),
    );
    sky.position.set(0, 120, -62);
    sky.renderOrder = -10;
    this.group.add(sky);

    const starGeo = new THREE.BufferGeometry();
    const rnd = lcg(42);
    const pts: number[] = [];
    const cols: number[] = [];
    const starPalette = [0xdfe8ff, 0xffe8c8, 0xc8e8ff, 0xffffff];
    for (let i = 0; i < 520; i++) {
      pts.push((rnd() - 0.5) * 1200, 40 + rnd() * 520, -58 + rnd() * 6);
      const c = new THREE.Color(starPalette[Math.floor(rnd() * starPalette.length)]);
      cols.push(c.r, c.g, c.b);
    }
    starGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    starGeo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
    const stars = new THREE.Points(
      starGeo,
      new THREE.PointsMaterial({
        vertexColors: true, size: 1.8, sizeAttenuation: false,
        transparent: true, opacity: 0.95, depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    stars.renderOrder = -9;
    this.group.add(stars);

    const cloudMat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.16, depthWrite: false,
    });
    for (let i = 0; i < 14; i++) {
      const w = 10 + rnd() * 26;
      const cloud = new THREE.Mesh(new THREE.BoxGeometry(w, 2.4, 1), cloudMat);
      cloud.position.set((rnd() - 0.5) * 260, 34 + rnd() * 80, -20 - rnd() * 20);
      cloud.renderOrder = -8;
      this.group.add(cloud);
    }
  }

  private buildDressing(): void {
    const spireMat = new THREE.MeshLambertMaterial({ color: this.planet.spire });
    const spireMat2 = new THREE.MeshLambertMaterial({
      color: new THREE.Color(this.planet.spire).offsetHSL(0, 0, 0.05),
    });
    const rnd2 = lcg(this.planet.seed + 9);
    for (let i = 0; i < 16; i++) {
      const far = i % 2 === 0;
      const h = 8 + rnd2() * 26;
      const spire = new THREE.Mesh(
        new THREE.ConeGeometry(2.5 + rnd2() * 5, h, 5),
        far ? spireMat : spireMat2,
      );
      spire.position.set(
        (rnd2() - 0.5) * (C.world.cols + 60),
        h / 2 - 1,
        far ? -16 - rnd2() * 10 : -7 - rnd2() * 4,
      );
      spire.rotation.y = rnd2() * Math.PI;
      this.group.add(spire);
    }

    const planetSphere = new THREE.Mesh(
      new THREE.SphereGeometry(26, 24, 18),
      new THREE.MeshBasicMaterial({ color: 0x5a4a86 }),
    );
    planetSphere.position.set(-160, 120, -55);
    this.group.add(planetSphere);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(40, 2.2, 8, 40),
      new THREE.MeshBasicMaterial({ color: 0x8a78b8, transparent: true, opacity: 0.55 }),
    );
    ring.position.copy(planetSphere.position);
    ring.rotation.x = Math.PI / 2.4;
    this.group.add(ring);
  }

  /** Little rocks and crystal shards standing on the surface line. */
  private buildSurfaceScatter(): void {
    const rnd = lcg(this.planet.seed + 77);
    const rockMat = new THREE.MeshLambertMaterial({
      color: new THREE.Color(this.planet.rocks[0]).offsetHSL(0, 0, 0.06),
    });
    const shardMat = new THREE.MeshBasicMaterial({
      color: 0x9fe8ff, transparent: true, opacity: 0.85,
    });
    for (let col = 0; col < C.world.cols; col += 1) {
      const roll = rnd();
      if (roll > 0.22) continue;
      const x = this.colToX(col);
      const y = this.surfaceYAt(x);
      if (roll < 0.16) {
        const s = 0.25 + rnd() * 0.55;
        const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(s, 0), rockMat);
        rock.position.set(x + (rnd() - 0.5) * 0.6, y + s * 0.5, 0.4 + rnd() * 0.8);
        rock.rotation.set(rnd() * 3, rnd() * 3, rnd() * 3);
        this.group.add(rock);
      } else {
        const h = 0.5 + rnd() * 0.9;
        const shard = new THREE.Mesh(new THREE.ConeGeometry(0.14, h, 4), shardMat);
        shard.position.set(x, y + h / 2, 0.5);
        shard.rotation.z = (rnd() - 0.5) * 0.5;
        this.group.add(shard);
      }
    }
  }

  /** Sky + ambient respond to the focus altitude/depth. */
  updateSky(focusY: number, ambient: THREE.AmbientLight, sun: THREE.DirectionalLight): void {
    const spaceCol = new THREE.Color(0x04050e);
    const lowCol = new THREE.Color(this.planet.skyLow);
    const highCol = new THREE.Color(this.planet.skyHigh);
    const caveCol = new THREE.Color(0x060509);

    if (focusY >= 0) {
      const t = THREE.MathUtils.clamp(focusY / C.orbit.altitude, 0, 1);
      this.skyUniforms.bottomColor.value.copy(lowCol).lerp(spaceCol, t * t);
      this.skyUniforms.topColor.value.copy(highCol).lerp(spaceCol, Math.min(1, t * 1.6));
      ambient.intensity = 0.75;
      sun.intensity = 1.0;
    } else {
      const t = THREE.MathUtils.clamp(-focusY / 60, 0, 1);
      this.skyUniforms.bottomColor.value.copy(lowCol).lerp(caveCol, t);
      this.skyUniforms.topColor.value.copy(highCol).lerp(caveCol, t);
      ambient.intensity = THREE.MathUtils.lerp(0.75, 0.14, t);
      sun.intensity = THREE.MathUtils.lerp(1.0, 0.12, t);
    }
  }
}
